import React from 'react';
import { Msg, Select } from '../../kit';
import { Labeled, inputCls } from '../emailKit';
import { Envelope, Sender } from './types';

/**
 * Who an e-mail is from: the From address (picked from the approved senders,
 * Email Center → Campaigns → Approved senders), From name and Reply-To; CC and
 * BCC only where a campaign has them. Shared by Campaigns, MEMAC triggers and
 * Saved templates (extracted from the Campaigns content step, 2026-10-14).
 *
 * An address that is not on the approved list (an older campaign copied in,
 * or a sender removed since) stays visible, marked, so nothing changes unseen;
 * the server refuses it at save or send.
 */
export const EnvelopeFields: React.FC<{
  value: Envelope;
  onChange: (patch: Partial<Envelope>) => void;
  senders: Sender[];
  /** Template mode: '' = the template's own sender. */
  allowTemplateDefault?: boolean;
  showCcBcc?: boolean;
  disabled?: boolean;
  sendersError?: string;
}> = ({ value, onChange, senders, allowTemplateDefault, showCcBcc, disabled, sendersError }) => {
  const v = value;
  const approved = senders.some(s => s.email === v.from_email);
  const options = [
    ...(allowTemplateDefault || !v.from_email ? [''] : []),
    ...senders.map(s => s.email),
    ...(v.from_email && !approved ? [v.from_email] : []),
  ];
  const labels: Record<string, string> = {
    '': allowTemplateDefault ? "The template's own sender" : 'Choose a sender…',
    ...Object.fromEntries(senders.map(s => [s.email, s.name ? `${s.name} <${s.email}>` : s.email])),
    ...(v.from_email && !approved ? { [v.from_email]: `${v.from_email} (not an approved sender)` } : {}),
  };
  const pick = (email: string) => {
    const s = senders.find(x => x.email === email);
    onChange({ from_email: email, from_name: s ? s.name : (email ? v.from_name : '') });
  };
  return (
    <div className="flex flex-col gap-2">
      <fieldset disabled={disabled} className="flex flex-wrap items-end gap-3">
        <Labeled label="From (approved senders)">
          <Select value={v.from_email} onChange={pick} options={options} labels={labels} />
        </Labeled>
        <Labeled label="From name">
          <input value={v.from_name} maxLength={100} onChange={e => onChange({ from_name: e.target.value })}
            placeholder={allowTemplateDefault && !v.from_email ? "The template's" : 'e.g. Founders at UniversityTech.io'}
            className={`${inputCls} !w-56`} />
        </Labeled>
        <Labeled label="Reply-To (optional)">
          <input value={v.reply_to} onChange={e => onChange({ reply_to: e.target.value })}
            placeholder="Name <address> or address" className={`${inputCls} !w-56`} />
        </Labeled>
        {showCcBcc && (
          <>
            <Labeled label="CC (optional, up to 10)">
              <input value={v.cc || ''} onChange={e => onChange({ cc: e.target.value })}
                placeholder="a@x.com, b@y.com" className={`${inputCls} !w-56`} />
            </Labeled>
            <Labeled label="BCC (optional, up to 10)">
              <input value={v.bcc || ''} onChange={e => onChange({ bcc: e.target.value })}
                placeholder="a@x.com" className={`${inputCls} !w-56`} />
            </Labeled>
          </>
        )}
      </fieldset>
      {v.from_email && !approved && senders.length > 0 && (
        <div className="text-xs text-amber-800">
          {v.from_email} is not an approved sender: pick one from the list, or add it under Approved senders.
        </div>
      )}
      {sendersError && <Msg kind="error">Approved senders: {sendersError}</Msg>}
    </div>
  );
};

export default EnvelopeFields;
